import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {BreakpointSize, Grid, useWindowSize} from '@primer/react-brand/esm'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {resolveGenericContentCta} from '../../../../brand/lib/types/contentful'

import SectionIntro from '../../_components/SectionIntro/SectionIntro'
import Toggle from '../../_components/Toggle/Toggle'

import {Spacer} from '../../_components/Spacer/Spacer'
import {wait} from '../../utils/time'

import type Assets from '../../webgl-utils/assets'
import type {HomeCustomerStoriesContent} from '../../content'

import {CustomerStory} from './CustomerStory'
import {CustomerStoriesCtaAction} from './CustomerStoriesCtaAction'

const ANALYTICS_ID = 'customer_stories'

type Props = {
  contentfulContent: HomeCustomerStoriesContent
  assetsRef: React.RefObject<Assets | null>
}

export function CustomerStoriesSection(props: Props) {
  const {
    contentfulContent: {content: customerStoriesSectionContent, storyCategories},
    assetsRef,
  } = props

  const [currentCategory, setCurrentCategory] = useState<number>(0)
  const [visibleCategory, setVisibleCategory] = useState<number>(currentCategory)
  const gridWrapperRef = useRef<HTMLDivElement>(null)
  const windowSize = useWindowSize()

  const visibleCategoryIndex = useMemo(() => Number(visibleCategory), [visibleCategory])
  const [toggleWrapperTopPadding, setToggleWrapperSpacing] = useState(0)

  const onCategoryChange = useCallback(
    async (category: number) => {
      if (!gridWrapperRef.current || category === currentCategory) return

      const grid = gridWrapperRef.current.querySelector('.lp-CustomerStories-grid')
      if (!grid) return

      setVisibleCategory(category)
      grid.classList.add('lp-CustomerStories-grid--hidden')
      await wait(250)

      setCurrentCategory(category)
      await wait(250)

      grid.classList.remove('lp-CustomerStories-grid--hidden')
    },
    [currentCategory],
  )

  // toggleWrapper spacing
  // This adds extra padding in case the global and/or flash banner is shown
  useEffect(() => {
    const updateToggleWrapperSpacing = () => {
      if (windowSize?.width !== undefined && windowSize?.width <= 1011) {
        let extraHeaderHeight = document.querySelector('.global-banner')?.clientHeight || 0
        extraHeaderHeight += document.querySelector('.js-stale-session-flash')?.clientHeight || 0

        setToggleWrapperSpacing(extraHeaderHeight)
      }
    }

    updateToggleWrapperSpacing()

    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', updateToggleWrapperSpacing)

    return () => {
      window.removeEventListener('resize', updateToggleWrapperSpacing)
    }
  }, [windowSize.width])

  const currentCustomerStoriesCategory = storyCategories?.[currentCategory]
  const customerStoriesCta = customerStoriesSectionContent
    ? resolveGenericContentCta(customerStoriesSectionContent, {legacyLinksAsCta: true})
    : undefined

  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <section id="customer-stories" className="lp-Section">
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <div className="lp-CustomerStories-stickyContainer">
        {customerStoriesSectionContent ? (
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          <div className="lp-SectionBlock lp-SectionBlock--noBorder">
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            <div className="lp-SectionBlock-content">
              <SectionIntro
                mascot="ducky"
                title={`${customerStoriesSectionContent.fields.heading} <span>${
                  customerStoriesSectionContent.fields.subheading
                    ? documentToPlainTextString(customerStoriesSectionContent.fields.subheading)
                    : ''
                }</span>`}
                assetsRef={assetsRef}
                bottomSpacerSize="small"
                isWebGLMascotOnly
              />
            </div>
          </div>
        ) : null}

        {storyCategories ? (
          <div
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            className="lp-CustomerStories-toggleWrapper"
            style={{
              paddingTop:
                toggleWrapperTopPadding && toggleWrapperTopPadding > 0 ? `${toggleWrapperTopPadding}px` : undefined,
            }}
          >
            <Toggle
              currentIndex={visibleCategoryIndex}
              items={storyCategories.map(({category}, index) => ({
                label: category.fields.heading || '',
                value: `${index}`,
              }))}
              onToggle={(value: string) => onCategoryChange(Number(value))}
              hasBackground
            />
          </div>
        ) : null}

        <Spacer size="32px" size1012="56px" />

        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <hr className="lp-CustomerStories-sectionBlockDivider" />

        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div ref={gridWrapperRef} className="lp-SectionBlock">
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <div className="lp-SectionBlock-content lp-SectionBlock-content--lines lp-SectionBlock-content--narrow">
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            <Grid className="lp-CustomerStories-grid lp-CustomerStories-grid--customerStories">
              {currentCustomerStoriesCategory ? (
                <>
                  {currentCustomerStoriesCategory.stories.map((story, index) => (
                    <CustomerStory key={story.sys.id} story={story} analyticsId={ANALYTICS_ID} index={index} />
                  ))}

                  {/* Add empty column to fill the grid if needed */}
                  {[BreakpointSize.LARGE, BreakpointSize.XLARGE, BreakpointSize.XXLARGE].includes(
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    windowSize.currentBreakpointSize!,
                  ) &&
                    currentCustomerStoriesCategory.stories.length % 3 !== 0 && (
                      <Grid.Column
                        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                        className="lp-CustomerStories-gridColumn lp-CustomerStories-gridColumn--dummy"
                        span={{xsmall: 12, medium: 6, large: 4}}
                      />
                    )}
                </>
              ) : null}
            </Grid>
          </div>
        </div>
      </div>

      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <div className="lp-SectionBlock lp-SectionBlock--noBorder">
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className="lp-SectionBlock-content">
          <Spacer size="0px" size1012="48px" />

          {customerStoriesCta ? (
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            <div className="lp-CustomerStories-links">
              <div>
                <CustomerStoriesCtaAction action={customerStoriesCta.primary} location={ANALYTICS_ID} />
              </div>

              {customerStoriesCta.secondary ? (
                <>
                  {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                  <hr className="lp-CustomerStories-divider" />

                  <div>
                    <CustomerStoriesCtaAction action={customerStoriesCta.secondary} location={ANALYTICS_ID} />
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
